import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../../ui/dialog";
import { Button } from "../../../../ui/button";
import { Badge } from "../../../../ui/badge";
import { Separator } from "../../../../ui/separator";
import { GlobalVariableRegistry, DecisionCondition } from "../../utils/types";
import { renderContentWithPlaceholders, resolveIdentifierToNodeId } from "../../utils/utils";
import { PreviewModalState } from "../../hooks/hooks";

interface PreviewModalProps {
  previewModalState: PreviewModalState;
  globalVariableRegistry: GlobalVariableRegistry;
  nodes: any[];
  setPreviewModalState: (state: PreviewModalState | ((prev: PreviewModalState) => PreviewModalState)) => void;
  onClose: () => void;
  onPlaceholderClick: (nodeId: string, placeholderName: string) => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  previewModalState,
  globalVariableRegistry,
  nodes,
  setPreviewModalState,
  onClose,
  onPlaceholderClick,
}) => {
  // Helper function to convert technical operators to human-readable text
  const getReadableOperator = (operator: string, value?: string): string => {
    switch (operator) {
      case "is_true":
        return "is true";
      case "is_false":
        return "is false";
      case "==":
        return `equals "${value}"`;
      case "!=":
        return `does not equal "${value}"`;
      case "contains":
        return `contains "${value}"`;
      case ">":
        return `is greater than ${value}`;
      case "<":
        return `is less than ${value}`;
      case ">=":
        return `is greater than or equal to ${value}`;
      case "<=":
        return `is less than or equal to ${value}`;
      default:
        return `${operator} ${value || ""}`;
    }
  };

  // Helper function to resolve node reference to node for display
  const resolveNodeForDisplay = (nodeReference: string | null): any => {
    if (!nodeReference) return null;
    
    // First try to resolve by identifier, then by direct ID lookup
    const resolvedId = resolveIdentifierToNodeId(nodeReference, nodes);
    if (resolvedId) {
      return nodes.find(n => n.id === resolvedId);
    }
    
    // Fallback: direct lookup by ID
    return nodes.find(n => n.id === nodeReference);
  };

  // Helper function to determine which path was taken through a decision node
  const getActualPathTaken = (decisionNode: any, nextNodeInPath: any): "true" | "false" | null => {
    if (!decisionNode?.data?.decisionData || !nextNodeInPath) return null;
    
    const { truePath, falsePath } = decisionNode.data.decisionData;
    
    // Check if next node matches true path
    if (truePath) {
      const truePathNode = resolveNodeForDisplay(truePath);
      if (truePathNode && truePathNode.id === nextNodeInPath.id) {
        return "true";
      }
    }
    
    // Check if next node matches false path  
    if (falsePath) {
      const falsePathNode = resolveNodeForDisplay(falsePath);
      if (falsePathNode && falsePathNode.id === nextNodeInPath.id) {
        return "false";
      }
    }
    
    return null;
  };

  // Helper function to get the negated/opposite operator
  const getNegatedOperator = (operator: string, value?: string): string => {
    switch (operator) {
      case "is_true":
        return "is false";
      case "is_false":  
        return "is true";
      case "==":
        return `does not equal "${value}"`;
      case "!=":
        return `equals "${value}"`;
      case "contains":
        return `does not contain "${value}"`;
      case ">":
        return `is less than or equal to ${value}`;
      case "<":
        return `is greater than or equal to ${value}`;
      case ">=":
        return `is less than ${value}`;
      case "<=":
        return `is greater than ${value}`;
      default:
        return `not (${operator} ${value || ""})`;
    }
  };
  return (
    <Dialog open={previewModalState.visible} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden" style={{ zIndex: 1000 }}>
        <DialogHeader>
          <DialogTitle className="text-lg font-gc-med text-foreground">
            Conversation Preview
          </DialogTitle>
        </DialogHeader>
        
        <div className="max-h-[calc(80vh-180px)] overflow-y-auto p-3 pr-6 rounded bg-background">
          {/* Show conversation path selector if there are multiple paths */}
          {previewModalState.conversationPaths && previewModalState.conversationPaths.length > 1 && (
            <div className="mb-3">
              <p className="text-xs text-muted-foreground mb-2 block">
                📍 Multiple conversation paths available - click to switch:
              </p>
              <div className="flex flex-wrap gap-2">
                {previewModalState.conversationPaths.map((path: any, index: number) => (
                  <Button
                    key={index}
                    variant={previewModalState.activePathIndex === index ? "default" : "outline"}
                    size="sm"
                    className="rounded-full min-w-[40px] h-7 text-xs"
                    onClick={() => {
                      setPreviewModalState((prev: PreviewModalState) => ({
                        ...prev,
                        activePathIndex: index,
                        conversationPath: path // Update the current path for backward compatibility
                      }));
                    }}
                    title={`View conversation path ${index + 1} (${path.length} message${path.length !== 1 ? "s" : ""})`}
                  >
                    Path {index + 1}
                  </Button>
                ))}
              </div>
            </div>
          )}
        
          {/* Message count display */}
          <div className="my-4">
            <Separator className="mb-2" />
            <p className="text-sm text-muted-foreground text-center">
              {previewModalState.conversationPath.length === 1 
                ? "1 message in conversation" 
                : `${previewModalState.conversationPath.length} messages in conversation`}
            </p>
            <Separator className="mt-2" />
          </div>
        
        {(previewModalState.conversationPath || []).map((node: any, index: number) => {
          const isUser = node.data.entityType === "USER";
          const isDecision = node.data.entityType === "DECISION";
          
          // For Decision nodes, determine which path was actually taken
          if (isDecision) {
            // Find the next node in the conversation path to determine which branch was taken
            const nextNodeInPath = index < previewModalState.conversationPath.length - 1 
              ? previewModalState.conversationPath[index + 1] 
              : null;
            
            const pathTaken = getActualPathTaken(node, nextNodeInPath);
            
            return (
              <div 
                key={node.id}
                style={{ 
                  display: "flex", 
                  flexDirection: "column",
                  marginBottom: "20px",
                  position: "relative",
                  alignItems: "center"
                }}
              >
                {/* Decision indicator with arrow flow */}
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  marginBottom: "12px",
                  padding: "8px 16px",
                  backgroundColor: "#f0f0f0",
                  borderRadius: "20px",
                  border: "1px solid #d9d9d9"
                }}>
                  <span className="text-xs font-gc-med uppercase tracking-wide" style={{ color: "#722ed1" }}>
                    🔀 Decision Point
                  </span>
                </div>
                
                <div style={{
                  width: "80%",
                  maxWidth: "400px",
                  padding: "16px",
                  borderRadius: "8px",
                  backgroundColor: "#fafafa",
                  border: "2px dashed #d3adf7",
                  position: "relative"
                }}>
                  {/* Condition display - shows actual path taken or raw conditions */}
                  <div>
                    {node.data.decisionData?.conditions && node.data.decisionData.conditions.length > 0 ? (
                      <>
                        {node.data.decisionData.conditions.map((condition: DecisionCondition, condIndex: number) => {
                          // If no path was taken (decision node at end), show raw conditions
                          if (pathTaken === null) {
                            return (
                              <div key={condition.id || `${condition.variable}-${condition.operator}-${condition.value}-${condIndex}`} style={{ 
                                marginBottom: condIndex < node.data.decisionData.conditions.length - 1 ? "8px" : "0", 
                                padding: "12px 16px",
                                backgroundColor: "white",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0",
                                fontSize: "14px"
                              }}>
                                {condIndex === 0 && (
                                  <span style={{ 
                                    color: "#666", 
                                    fontWeight: "500",
                                    fontSize: "13px" 
                                  }}>
                                    🔍 Evaluates:
                                  </span>
                                )}
                                {condIndex > 0 && (
                                  <span style={{ 
                                    color: "#666", 
                                    fontWeight: "600",
                                    fontSize: "13px" 
                                  }}>
                                    {condition.joiner?.toUpperCase() || "AND"}
                                  </span>
                                )}
                                <div style={{ marginTop: condIndex === 0 ? "6px" : "4px" }}>
                                  <span style={{ fontWeight: "600", color: "#722ed1" }}>{condition.variable}</span>
                                  <span style={{ color: "#666", marginLeft: "6px" }}>{getReadableOperator(condition.operator, condition.value)}</span>
                                </div>
                              </div>
                            );
                          }
                          
                          // Path was taken - show met/not met condition
                          const showNegated = pathTaken === "false";
                          const operatorText = showNegated 
                            ? getNegatedOperator(condition.operator, condition.value)
                            : getReadableOperator(condition.operator, condition.value);
                          
                          return (
                            <div key={condition.id || `${condition.variable}-${condition.operator}-${condition.value}-${condIndex}`} style={{ 
                              marginBottom: condIndex < node.data.decisionData.conditions.length - 1 ? "8px" : "0", 
                              padding: "12px 16px",
                              backgroundColor: pathTaken === "true" ? "#f6ffed" : "#fff2f0",
                              borderRadius: "8px",
                              border: pathTaken === "true" ? "1px solid #b7eb8f" : "1px solid #ffccc7",
                              fontSize: "14px"
                            }}>
                              {condIndex === 0 && (
                                <span style={{ 
                                  color: "#666", 
                                  fontWeight: "500",
                                  fontSize: "13px" 
                                }}>
                                  {pathTaken === "true" ? "✅ Condition met:" : "❌ Condition not met:"}
                                </span>
                              )}
                              {condIndex > 0 && (
                                <span style={{ 
                                  color: "#666", 
                                  fontWeight: "600",
                                  fontSize: "13px" 
                                }}>
                                  {condition.joiner?.toUpperCase() || "AND"}
                                </span>
                              )}
                              <div style={{ marginTop: condIndex === 0 ? "6px" : "4px" }}>
                                <span style={{ fontWeight: "600", color: "#722ed1" }}>{condition.variable}</span>
                                <span style={{ color: "#666", marginLeft: "6px" }}>{operatorText}</span>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <div style={{ 
                        padding: "12px 16px",
                        backgroundColor: "#fff7e6",
                        borderRadius: "8px",
                        border: "1px solid #ffd591",
                        fontSize: "14px",
                        color: "#fa8c16"
                      }}>
                        No condition configured
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          }
          
          // For USER and BOT nodes, render normally
          return (
            <div 
              key={node.id}
              style={{ 
                display: "flex", 
                flexDirection: "column",
                marginBottom: "20px",
                position: "relative"
              }}
            >
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                marginBottom: "8px",
                marginLeft: isUser ? "0" : "auto",
                marginRight: isUser ? "auto" : "0",
                alignSelf: isUser ? "flex-start" : "flex-end",
                flexDirection: isUser ? "row" : "row-reverse"
              }}>
                <span 
                  className="text-sm font-gc-med"
                  style={{ color: isUser ? "#389e0d" : "#096dd9" }}
                >
                  {node.data.entityType}
                </span>
                {node.data.identifier && (
                  <Badge 
                    variant="secondary"
                    className="text-xs"
                    style={{ 
                      marginLeft: isUser ? 8 : 0, 
                      marginRight: isUser ? 0 : 8,
                      backgroundColor: "#fff7e6",
                      color: "#d46b08",
                      border: "1px solid #ffd591"
                    }}
                  >
                    {node.data.identifier}
                  </Badge>
                )}
                {node.data.isDestinationNode && (
                  <Badge 
                    variant="destructive"
                    className="text-xs"
                    style={{ 
                      marginLeft: isUser ? 8 : 0, 
                      marginRight: isUser ? 0 : 8
                    }}
                  >
                    DESTINATION
                  </Badge>
                )}
              </div>
              
              <div style={{
                maxWidth: "95%",
                padding: "12px 16px",
                borderRadius: "12px",
                backgroundColor: isUser 
                  ? "#f6ffed" 
                  : "#e6f7ff",
                border: `1px solid ${isUser ? "#b7eb8f" : "#91d5ff"}`,
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                alignSelf: isUser ? "flex-start" : "flex-end",
                position: "relative"
              }}>
                <div style={{ 
                  fontSize: "15px", 
                  lineHeight: "1.5",
                  color: "rgba(0, 0, 0, 0.85)",
                  whiteSpace: "pre-wrap"
                }}>
                  {node.data.content ? (
                    renderContentWithPlaceholders(
                      node.data.content, 
                      node.data.placeholders || {}, 
                      globalVariableRegistry,
                      (name) => onPlaceholderClick(node.id, name)
                    )
                  ) : node.data.toolCalls && node.data.toolCalls.length > 0 ? (
                    <div>
                      <div style={{ 
                        marginBottom: "8px", 
                        fontWeight: "600", 
                        color: "#096dd9",
                        fontSize: "13px"
                      }}>
                        🔧 Tool Call:
                      </div>
                      {node.data.toolCalls.map((toolCall: any, index: number) => (
                        <div key={toolCall.id || `toolcall-${index}`} style={{
                          padding: "8px 12px",
                          backgroundColor: "#f0f8ff",
                          border: "1px solid #b3d9ff",
                          borderRadius: "6px",
                          marginBottom: index < node.data.toolCalls.length - 1 ? "8px" : "0",
                          fontSize: "13px"
                        }}>
                          <div style={{ fontWeight: "600", color: "#0066cc", marginBottom: "4px" }}>
                            {toolCall.name || "Unknown Function"}
                          </div>
                          {toolCall.parameters && toolCall.parameters.length > 0 && (
                            <div style={{ color: "#666", fontSize: "12px" }}>
                              <div style={{ marginBottom: "4px" }}>Parameters:</div>
                              {toolCall.parameters.map((param: any, paramIndex: number) => (
                                <div key={param.name || `param-${paramIndex}`} style={{ marginLeft: "8px", fontSize: "11px" }}>
                                  • <span style={{ fontWeight: "500" }}>{param.name}:</span> {param.value}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {toolCall.responseVariables && toolCall.responseVariables.length > 0 && (
                            <div style={{ color: "#666", fontSize: "12px", marginTop: "8px" }}>
                              <div style={{ marginBottom: "4px" }}>Response Variables:</div>
                              {toolCall.responseVariables.map((rv: any, rvIndex: number) => (
                                <div key={rv.name || `rv-${rvIndex}`} style={{ marginLeft: "8px", fontSize: "11px" }}>
                                  • <span style={{ fontWeight: "500", color: "#9c27b0" }}>{rv.name}:</span> {rv.values?.length || 0} values
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      color: "#999", 
                      fontStyle: "italic",
                      fontSize: "13px"
                    }}>
                      No content
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        
          {previewModalState.conversationPath.length === 0 && (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No messages to display</p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewModal; 